/* eslint-disable tailwindcss/no-custom-classname */
import {
  PropsOf,
  Theme,
  chakra,
  forwardRef,
  useStyleConfig,
} from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';

import { OmitChakraProps } from '../../utils/typeUtils';

import { $groupSpacingX, $groupSpacingY } from './Group.style';

interface BaseGroupProps extends OmitChakraProps<PropsOf<'div'>> {
  /**
   * Determines whether the flex container should fill its parent
   */
  size?: 'auto' | 'fill';
  /**
   * Determines the amount of space between flex items.
   */
  gap?: keyof Theme['space'];
}

type JustifyProps =
  | {
      /**
       * Determines the axis that items are placed in the flex container.
       *
       * @default horizontal
       */
      orientation: 'vertical';
      /**
       * Determines the alignment of items across the y-axis.
       */
      align?: 'start' | 'center' | 'end' | 'between';
      /**
       * Determines the justification of items across the x-axis.
       */
      justify?: 'start' | 'center' | 'end';
    }
  | {
      /**
       * Determines the axis that items are placed in the flex container.
       *
       * @default horizontal
       */
      orientation?: 'horizontal';
      /**
       * Determines the alignment of items across the y-axis.
       */
      align?: 'start' | 'center' | 'end';
      /**
       * Determines the justification of items across the x-axis.
       */
      justify?: 'start' | 'center' | 'end' | 'between';
    };

export type GroupProps = BaseGroupProps & JustifyProps;

/**
 * Context aware flex container that allows parent components to set default
 * gaps, alignment, and justification.
 */
export const Group = forwardRef<GroupProps, 'div'>(
  (
    {
      align,
      className,
      justify,
      orientation = 'horizontal',
      gap,
      ...props
    }: GroupProps,
    ref,
  ) => {
    const styles = useStyleConfig('Group', props);
    const spacingVar =
      orientation === 'horizontal' ? $groupSpacingX : $groupSpacingY;

    return (
      <chakra.div
        {...props}
        className={classnames('orbit-group', className)}
        data-direction={orientation}
        data-align={align}
        data-justify={justify}
        ref={ref}
        sx={{ [spacingVar.variable]: gap ? `space.${gap}` : undefined }}
        __css={styles}
      />
    );
  },
);
